<script setup lang="ts">
  import IconChevronRight from '../icons/IconChevronRight.vue'
  import { computed } from 'vue'
  const pageLimit = 5
  const props = defineProps({
    modelValue: {
      type: Number,
      default: 1,
    },
    total: {
      type: Number,
      default: 1,
    },
    disabled: {
      type: Boolean,
      default: false,
    },
  })
  const emit = defineEmits(['update:modelValue'])
  const currentPage = computed({
    get() {
      return props.modelValue
    },
    set(newValue) {
      emit('update:modelValue', newValue)
    },
  })
  function goPrevPage() {
    currentPage.value--
  }
  function goNextPage() {
    currentPage.value++
  }
  function goToPage(number: number) {
    currentPage.value = number
  }
  function isPageVisible(number: number) {
    if (number === 1 || number === props.total) return true
    const currentPageNumber = currentPage.value + 1
    if (currentPageNumber >= pageLimit) {
      // End numbers case
      if (currentPageNumber > props.total - pageLimit + 1) {
        return number > props.total - pageLimit
      }
      // Middle numbers case
      return number === currentPageNumber + 1 || number === currentPageNumber || number === currentPageNumber - 1
    } else {
      // First numbers case
      return number <= pageLimit
    }
  }
  function isDotsVisible(number: number) {
    const currentPageNumber = currentPage.value + 1
    if (currentPageNumber >= pageLimit) {
      return number === 2 || number === props.total - 1
    } else {
      // After 1st nth pages before the last page
      return currentPage.value < pageLimit - 1 && number === pageLimit + 1 && number !== props.total
    }
  }
</script>
<template>
  <div id="pagination-info" class="d-flex align-center">
    <div aria-atomic="true">Page {{ currentPage + 1 }} of {{ total }}</div>
    <div class="d-flex align-center">
      <button
        class="transparent-btn"
        :disabled="currentPage === 0"
        :aria-label="currentPage === 0 ? 'Previous page (unavailable)' : `Go to previous page`"
        @click="goPrevPage"
      >
        <IconChevronLeft />
      </button>
      <div
        class="page-number d-flex align-center"
        role="group"
        aria-labelledby="pagination-info"
        aria-label="Page navigation buttons"
      >
        <template v-for="item in total" :key="`page-btn-${item}`">
          <button
            v-if="isPageVisible(item)"
            class="page-btn"
            :disabled="item === currentPage + 1"
            :class="item === currentPage + 1 ? 'active' : ''"
            :aria-label="currentPage === 0 ? 'Previous page (unavailable)' : `Go to previous page, page ${currentPage}`"
            @click="goToPage(item - 1)"
          >
            {{ item }}
          </button>
          <div v-else-if="isDotsVisible(item)">...</div>
        </template>
      </div>
      <button
        class="transparent-btn"
        :aria-label="currentPage === total - 1 ? 'Next page (unavailable)' : `Go to next page, page ${currentPage + 2}`"
        :disabled="currentPage === total - 1"
        @click="goNextPage"
      >
        <IconChevronRight />
      </button>
    </div>
  </div>
</template>
<style>
  .transparent-btn {
    background-color: transparent;
    border: none;
    outline: none;
    cursor: pointer;
    transition: all 0.2s ease;
    color: var(--color-super-dark-grey);

    &:hover {
      color: var(--color-black);
    }

    &:disabled {
      cursor: not-allowed;
      opacity: 0.5;
    }
  }

  .page-btn {
    color: var(--color-super-dark-grey);
    border: 1px solid transparent;
    background-color: transparent;
    border-radius: 3px;
    outline: none;
    transition: all 0.2s ease;
    cursor: pointer;
    margin-right: 2px;

    &.active {
      border-color: var(--color-light-orange);
      cursor: default;
      font-weight: 500;
      color: var(--color-black);
    }

    &:hover:not(.active) {
      background-color: #e1e3e6;
    }
  }
</style>
